import { useEffect, useMemo, useRef, useState } from 'react'
import { serversApi } from '../../api'
import { getErrorMessage } from '../../api/client'
import type { EdgeConfigResult, EdgeDevice, EdgeTagEdit } from '../../types'

// 디바이스 태그 편집 — 목록 통째 교체 (엣지 UpdateDeviceTags). 엑셀에서 복사한 표를 붙여넣어 한 번에 넣을 수 있다.
// 기존 태그는 id 를 유지해 마스터데이터 연결이 살아 있고, 지운 태그를 가리키던 항목은 엣지에서 끊긴다.

// 엣지 TagDataType 이름 — [Description] 한글 이름도 붙여넣기에서 받아 준다
const DATA_TYPES = ['Bit', 'Word', 'UWord', 'Bcd', 'Ascii', 'CrossAscii', 'CrossWord', 'Float', 'CrossFloat', 'Real', 'Time', 'TimeSec', 'DateTime']
const DATA_TYPE_ALIASES: Record<string, string> = {
  '비트': 'Bit', '워드': 'Word', 'u워드': 'UWord', '교차 ascii': 'CrossAscii', 'date and time': 'DateTime', 'time(sec)': 'TimeSec',
}

function normalizeDataType(raw: string): string {
  const s = raw.trim()
  if (!s) return 'Bit'
  const lower = s.toLowerCase()
  return DATA_TYPES.find(t => t.toLowerCase() === lower) ?? DATA_TYPE_ALIASES[lower] ?? s
}

// Modbus 주소는 엣지 형식 "국번-기능코드x주소" — 엑셀의 "1, 3x0" / "1 3x0" 도 받아 준다
const isModbus = (device: EdgeDevice) => /modbus/i.test(device.channelType)

function normalizeAddress(raw: string, modbus: boolean): string {
  const s = raw.trim()
  if (!modbus) return s
  const m = /^(\d+)\s*[,\s-]\s*(\d+)\s*x\s*(\d+)$/i.exec(s)
  return m ? `${m[1]}-${m[2]}x${m[3]}` : s
}

interface Row {
  key: string // 화면 행 키 (기존 태그면 태그 id)
  id: string | null
  address: string
  size: string
  dataType: string
  arithmetic: string
  description: string // 엣지 메모
  alias: string // 태그 별칭 — 마스터데이터 전송코드 {설비코드}_{Alias} 의 재료
}

// 붙여넣은 행 — 별칭 열을 고르지 않았으면 alias 가 없고, 같은 주소의 기존 별칭을 그대로 둔다
type PastedRow = Omit<Row, 'key' | 'id' | 'alias'> & { alias?: string }

type RowErrors = Partial<Record<'address' | 'size' | 'dataType', string>>

let newRowSeq = 0
const newKey = () => `new-${++newRowSeq}`

const emptyRow = (): Row => ({ key: newKey(), id: null, address: '', size: '1', dataType: 'Bit', arithmetic: '', description: '', alias: '' })

// 엣지(Em2ConfigHandlers.UpdateDeviceTags)와 같은 규칙 — 저장 전에 화면에서 먼저 알려준다
function validate(rows: Row[]) {
  const errors: Record<string, RowErrors> = {}
  const count = new Map<string, number>()
  rows.forEach(r => count.set(r.address.trim(), (count.get(r.address.trim()) ?? 0) + 1))
  for (const r of rows) {
    const e: RowErrors = {}
    if (!r.address.trim()) e.address = '필수'
    else if ((count.get(r.address.trim()) ?? 0) > 1) e.address = '중복'
    const size = Number(r.size)
    if (!Number.isInteger(size) || size < 1) e.size = '1 이상'
    if (!DATA_TYPES.includes(r.dataType)) e.dataType = '알 수 없음'
    if (Object.keys(e).length > 0) errors[r.key] = e
  }
  return errors
}

const toEdit = (r: Row): EdgeTagEdit => ({
  id: r.id,
  address: r.address.trim(),
  size: Number(r.size),
  dataType: r.dataType,
  arithmetic: r.arithmetic.trim() || null,
  description: r.description.trim() || null,
  alias: r.alias.trim(), // 빈 값이면 엣지 별칭을 지운다 (null 이어야 유지)
})

export default function TagEditor({ serverId, device, onClose, onSaved }: {
  serverId: string
  device: EdgeDevice
  onClose: () => void
  onSaved: (result: EdgeConfigResult) => void
}) {
  const initial = useMemo<Row[]>(() => device.tags.map(t => ({
    key: t.id,
    id: t.id,
    address: t.address ?? '',
    size: String(t.size),
    dataType: t.dataType,
    arithmetic: t.arithmetic ?? '',
    description: t.description ?? '',
    alias: t.alias ?? '',
  })), [device])
  const [rows, setRows] = useState<Row[]>(initial)
  const [pasteOpen, setPasteOpen] = useState(device.tags.length === 0)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [showErrors, setShowErrors] = useState(false)
  const [copied, setCopied] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)

  const dirty = JSON.stringify(rows.map(toEdit)) !== JSON.stringify(initial.map(toEdit))
  const errors = validate(rows)
  const errorCount = Object.keys(errors).length
  const removedCount = initial.filter(r => !rows.some(x => x.id === r.id)).length

  const requestClose = () => {
    if (dirty && !confirm('저장하지 않은 변경 사항이 있습니다. 닫을까요?')) return
    onClose()
  }
  const closeRef = useRef(requestClose)
  useEffect(() => { closeRef.current = requestClose })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const update = (key: string, patch: Partial<Row>) => setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r)))
  const remove = (key: string) => setRows(rs => rs.filter(r => r.key !== key))

  const addRow = () => {
    const row = emptyRow()
    setRows(rs => [...rs, row])
    requestAnimationFrame(() => {
      listRef.current?.querySelector<HTMLInputElement>(`[data-row="${row.key}"] input`)?.focus()
    })
  }

  // 붙여넣은 행 반영 — 같은 주소의 행이 있으면 그 행을 고치고(태그 id 유지), 없으면 뒤에 붙인다.
  // replace 면 붙여넣은 행만 남긴다 (같은 주소였던 기존 태그는 id 를 이어받는다)
  const applyPasted = (pasted: PastedRow[], replace: boolean) => {
    setRows(rs => {
      const byAddress = new Map(rs.map(r => [r.address.trim(), r]))
      const merged = pasted.map(p => {
        const old = byAddress.get(p.address)
        return { ...p, alias: p.alias ?? old?.alias ?? '', key: old?.key ?? newKey(), id: old?.id ?? null }
      })
      if (replace) return merged
      const mergedByKey = new Map(merged.map(m => [m.key, m]))
      const kept = rs.map(r => mergedByKey.get(r.key) ?? r)
      return [...kept, ...merged.filter(m => !rs.some(r => r.key === m.key))]
    })
    setPasteOpen(false)
  }

  // 지금 목록을 엑셀에 붙여넣을 수 있는 표(TSV)로 복사
  const copyAsTable = async () => {
    const lines = [['주소', '크기', '타입', '연산', '설명', 'Alias'], ...rows.map(r => [r.address, r.size, r.dataType, r.arithmetic, r.description, r.alias])]
    await navigator.clipboard.writeText(lines.map(l => l.map(c => (/[\t\n"]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join('\t')).join('\n'))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const save = async () => {
    if (errorCount > 0) {
      setShowErrors(true)
      return
    }
    if (removedCount > 0 && !confirm(`태그 ${removedCount}개를 삭제합니다. 이 태그를 쓰던 마스터데이터 항목도 연결이 끊깁니다. 저장할까요?`)) return
    setSaving(true)
    setSaveError(null)
    try {
      const { data } = await serversApi.updateDeviceTags(serverId, device.code, rows.map(toEdit))
      onSaved(data)
    } catch (err) {
      setSaveError(getErrorMessage(err, '저장하지 못했습니다.'))
      setSaving(false)
    }
  }

  const cellClass = (invalid: boolean) =>
    `w-full border rounded px-2 py-1 text-xs ${invalid ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-blue-400'} outline-none`

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onMouseDown={requestClose}>
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-6xl max-h-[85vh] flex flex-col overflow-hidden"
        onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-gray-800">태그 편집</h3>
          <span className="text-sm text-gray-600">{device.name}</span>
          <span className="font-mono text-xs text-gray-400">{device.code}</span>
          <span className="text-xs text-gray-400 ml-1">{rows.length}개</span>
          <button onClick={requestClose} className="ml-auto text-gray-400 hover:text-gray-600" aria-label="닫기">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-gray-100 shrink-0">
          <button onClick={addRow}
            className="border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50 transition">+ 행 추가</button>
          <button onClick={() => setPasteOpen(o => !o)}
            className={`border px-3 py-1.5 rounded-lg text-xs transition ${pasteOpen ? 'border-blue-400 text-blue-700 bg-blue-50' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            엑셀에서 붙여넣기
          </button>
          <button onClick={copyAsTable} disabled={rows.length === 0}
            className="border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50 transition disabled:opacity-40">
            {copied ? '복사됨 ✓' : '엑셀용으로 복사'}
          </button>
          {isModbus(device) && (
            <span className="text-xs text-gray-400">Modbus 주소: 국번-기능코드x주소 (예: 1-3x0). "1, 3x0" 으로 붙여넣어도 바꿔 줍니다.</span>
          )}
        </div>

        {pasteOpen && <PastePanel modbus={isModbus(device)} hasRows={rows.length > 0} onApply={applyPasted} onCancel={() => setPasteOpen(false)} />}

        <div ref={listRef} className="min-h-0 flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2 w-8">#</th>
                <th className="text-left px-2 py-2">주소 <span className="text-red-500">*</span></th>
                <th className="text-left px-2 py-2 w-20">크기</th>
                <th className="text-left px-2 py-2 w-32">타입</th>
                <th className="text-left px-2 py-2 w-40">연산</th>
                <th className="text-left px-2 py-2">설명</th>
                <th className="text-left px-2 py-2 w-48" title="태그 별칭 — 마스터데이터 전송코드 {설비코드}_{Alias}">Alias</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400 text-sm">태그가 없습니다. 행을 추가하거나 엑셀에서 붙여넣으세요.</td></tr>
              ) : rows.map((r, i) => {
                const e = showErrors ? errors[r.key] ?? {} : {}
                return (
                  <tr key={r.key} data-row={r.key} className={r.id ? '' : 'bg-blue-50/40'}>
                    <td className="px-3 py-1.5 text-xs text-gray-400">{i + 1}</td>
                    <td className="px-2 py-1.5">
                      <input value={r.address} onChange={ev => update(r.key, { address: ev.target.value })}
                        onBlur={ev => update(r.key, { address: normalizeAddress(ev.target.value, isModbus(device)) })}
                        aria-invalid={!!e.address} title={e.address} className={`${cellClass(!!e.address)} font-mono`} />
                    </td>
                    <td className="px-2 py-1.5">
                      <input value={r.size} onChange={ev => update(r.key, { size: ev.target.value })}
                        aria-invalid={!!e.size} title={e.size} className={cellClass(!!e.size)} />
                    </td>
                    <td className="px-2 py-1.5">
                      <select value={r.dataType} onChange={ev => update(r.key, { dataType: ev.target.value })}
                        aria-invalid={!!e.dataType} title={e.dataType} className={cellClass(!!e.dataType)}>
                        {!DATA_TYPES.includes(r.dataType) && <option value={r.dataType}>{r.dataType}</option>}
                        {DATA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1.5">
                      <input value={r.arithmetic} onChange={ev => update(r.key, { arithmetic: ev.target.value })}
                        className={`${cellClass(false)} font-mono`} />
                    </td>
                    <td className="px-2 py-1.5">
                      <input value={r.description} onChange={ev => update(r.key, { description: ev.target.value })}
                        className={cellClass(false)} />
                    </td>
                    <td className="px-2 py-1.5">
                      <input value={r.alias} onChange={ev => update(r.key, { alias: ev.target.value })}
                        className={`${cellClass(false)} font-mono`} />
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <button onClick={() => remove(r.key)} className="text-gray-300 hover:text-red-500" aria-label="행 삭제">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-t border-gray-100 shrink-0">
          <span className="text-xs text-gray-500">
            {rows.filter(r => !r.id).length > 0 && <>새 태그 {rows.filter(r => !r.id).length}개 · </>}
            {removedCount > 0 && <span className="text-amber-600">삭제 {removedCount}개 · </span>}
            {showErrors && errorCount > 0 ? <span className="text-red-600">확인이 필요한 행 {errorCount}개</span> : dirty ? '변경됨' : '변경 없음'}
          </span>
          {saveError && <span className="text-xs text-red-600">{saveError}</span>}
          <div className="ml-auto flex gap-2">
            <button onClick={requestClose} disabled={saving}
              className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition disabled:opacity-50">취소</button>
            <button onClick={save} disabled={saving || !dirty}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition disabled:opacity-50">
              {saving ? '저장 중…' : 'Edge에 저장'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// 엑셀 붙여넣기 — 열마다 역할을 고른다. 첫 줄이 머리글이면 이름으로 알아서 맞춘다
// ═══════════════════════════════════════════════════════════════════════

type ColumnRole = 'address' | 'size' | 'dataType' | 'arithmetic' | 'description' | 'alias' | 'ignore'

const ROLE_LABELS: Record<ColumnRole, string> = {
  address: '주소', size: '크기', dataType: '타입', arithmetic: '연산', description: '설명', alias: 'Alias', ignore: '무시',
}

const HEADER_HINTS: [ColumnRole, RegExp][] = [
  ['address', /^(주소|address|addr|노드|node)/i],
  ['size', /^(크기|size|길이)/i],
  ['dataType', /^(타입|데이터\s*타입|data\s*type|type|형식)/i],
  ['arithmetic', /^(연산|arithmetic|수식|계산)/i],
  ['description', /^(설명|description|desc|항목명|항목|이름|명칭)/i],
  ['alias', /^(alias|별칭|mes\s*key)/i],
]

// 엑셀 복사 형식(TSV) — 셀 안에 줄바꿈·탭이 있으면 따옴표로 감싸 온다
function parseTsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false
  for (let i = 0; i < text.length; i++) {
    const c = text[i]
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') { cell += '"'; i++ }
      else if (c === '"') quoted = false
      else cell += c
    } else if (c === '"' && cell === '') quoted = true
    else if (c === '\t') { row.push(cell); cell = '' }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++
      row.push(cell); rows.push(row); row = []; cell = ''
    } else cell += c
  }
  if (cell !== '' || row.length > 0) { row.push(cell); rows.push(row) }
  return rows.filter(r => r.some(c => c.trim() !== ''))
}

function guessRoles(header: string[]): ColumnRole[] | null {
  const roles = header.map(h => HEADER_HINTS.find(([, re]) => re.test(h.trim()))?.[0] ?? 'ignore')
  return roles.includes('address') ? roles : null
}

// 머리글이 없으면 순서대로: 주소·크기·타입·연산·설명·Alias
const DEFAULT_ORDER: ColumnRole[] = ['address', 'size', 'dataType', 'arithmetic', 'description', 'alias']

function PastePanel({ modbus, hasRows, onApply, onCancel }: {
  modbus: boolean
  hasRows: boolean
  onApply: (rows: PastedRow[], replace: boolean) => void
  onCancel: () => void
}) {
  const [text, setText] = useState('')
  const [roles, setRoles] = useState<ColumnRole[]>([])
  const [hasHeader, setHasHeader] = useState(false)
  const [replace, setReplace] = useState(false)

  const table = useMemo(() => parseTsv(text), [text])
  const width = Math.max(0, ...table.map(r => r.length))
  const body = hasHeader ? table.slice(1) : table

  // 새로 붙여넣으면 열 역할을 다시 추측한다
  const onTextChange = (next: string) => {
    setText(next)
    const parsed = parseTsv(next)
    const guessed = parsed.length > 0 ? guessRoles(parsed[0]) : null
    const w = Math.max(0, ...parsed.map(r => r.length))
    setHasHeader(guessed != null)
    setRoles(guessed ?? Array.from({ length: w }, (_, i) => DEFAULT_ORDER[i] ?? 'ignore'))
  }

  const roleAt = (i: number): ColumnRole => roles[i] ?? 'ignore'
  const setRole = (i: number, role: ColumnRole) =>
    setRoles(rs => Array.from({ length: width }, (_, j) => (j === i ? role : rs[j] ?? 'ignore')))

  // 주소가 비어 있는 행은 건너뛴다 (병합 셀로 생긴 빈 행 등)
  const converted = body.map((cells): PastedRow => {
    const get = (role: ColumnRole) => {
      const i = roles.indexOf(role)
      return i >= 0 ? (cells[i] ?? '').trim() : ''
    }
    return {
      address: normalizeAddress(get('address'), modbus),
      size: get('size') || '1',
      dataType: normalizeDataType(get('dataType')),
      arithmetic: get('arithmetic'),
      description: get('description'),
      alias: roles.includes('alias') ? get('alias') : undefined,
    }
  }).filter(r => r.address)

  const canApply = roles.includes('address') && converted.length > 0

  return (
    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/60 shrink-0 max-h-[45vh] overflow-auto">
      <textarea value={text} onChange={e => onTextChange(e.target.value)} rows={text ? 3 : 4} autoFocus
        placeholder={'엑셀에서 범위를 복사(Ctrl+C)해 여기에 붙여넣으세요(Ctrl+V).\n열 순서는 아래에서 고를 수 있고, 첫 줄이 머리글(주소·크기·타입·연산·설명/항목명·Alias)이면 알아서 맞춥니다.'}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono bg-surface" />

      {table.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-gray-600">
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={hasHeader} onChange={e => setHasHeader(e.target.checked)} />
              첫 줄은 머리글
            </label>
            {hasRows && (
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={replace} onChange={e => setReplace(e.target.checked)} />
                기존 목록을 이 내용으로 교체 <span className="text-gray-400">(끄면 같은 주소는 고치고 나머지는 추가)</span>
              </label>
            )}
          </div>

          <div className="mt-2 overflow-x-auto border border-gray-200 rounded-lg bg-surface">
            <table className="text-xs">
              <thead className="bg-gray-50">
                <tr>
                  {Array.from({ length: width }, (_, i) => (
                    <th key={i} className="px-2 py-1.5 text-left font-normal">
                      <select value={roleAt(i)} onChange={e => setRole(i, e.target.value as ColumnRole)}
                        className={`border rounded px-1 py-0.5 ${roleAt(i) === 'ignore' ? 'border-gray-200 text-gray-400' : 'border-blue-300 text-blue-700'}`}>
                        {(Object.keys(ROLE_LABELS) as ColumnRole[]).map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                      </select>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {table.slice(0, 6).map((cells, ri) => (
                  <tr key={ri} className={hasHeader && ri === 0 ? 'text-gray-400 italic' : ''}>
                    {Array.from({ length: width }, (_, i) => (
                      <td key={i} className={`px-2 py-1 whitespace-nowrap max-w-[200px] truncate ${roleAt(i) === 'ignore' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {cells[i] ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {table.length > 6 && <div className="px-2 py-1 text-xs text-gray-400">… 외 {table.length - 6}줄</div>}
          </div>

          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-gray-500">
              {roles.includes('address') ? `태그 ${converted.length}개로 반영` : '주소 열을 고르세요'}
            </span>
            <div className="ml-auto flex gap-2">
              <button onClick={onCancel} className="border border-gray-300 text-gray-600 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50 transition">닫기</button>
              <button onClick={() => onApply(converted, replace)} disabled={!canApply}
                className="bg-gray-800 text-gray-50 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-700 transition disabled:opacity-40">
                목록에 반영
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
